import React from 'react';
import { Button, Input, InputRef, Space } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import Assets from 'assets/icons';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setAppliedFilters } from 'store/slices/dataDocumentsSlice';
import { fetchAvailableDocuments } from 'store/reducers/documentsCreator';
import { DocumentFilters } from 'interfaces';
import { serializeFiltersToUrl } from 'helpers/documentFiltersUrl';
import classes from './DocumentTable.module.scss';

interface Props {
  confirm?: () => void;
  clearFilters?: () => void;
  close: () => void;
  setSelectedKeys?: (keys: React.Key[]) => void;
  selectedKeys?: React.Key[];
  dataIndex?: string;
}

const FilterDropdown = ({ close }: Props) => {
  const { appliedFilters, currentFolder } = useAppSelector(
    (store) => store.documents,
  );
  const { dataRoom } = useAppSelector((store) => store.dataRoom);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchText, setSearchText] = React.useState<string>(
    appliedFilters.name || '',
  );
  const searchInput = React.useRef<InputRef>(null);

  React.useEffect(() => {
    setSearchText(appliedFilters.name || '');
  }, [appliedFilters.name]);

  React.useEffect(() => {
    searchInput.current?.focus();
  }, []);

  const handleSearch = () => {
    const trimmed = searchText.trim();
    const nextFilters: DocumentFilters = {
      ...appliedFilters,
      name: trimmed || null,
    };
    dispatch(setAppliedFilters(nextFilters));
    setSearchParams(serializeFiltersToUrl(searchParams, nextFilters), {
      replace: true,
    });
    dispatch(
      fetchAvailableDocuments({
        roomId: dataRoom!.id,
        folderId: currentFolder[0]?.id || null,
        filters: nextFilters,
      }),
    );
    close();
  };

  const handleReset = () => {
    setSearchText('');
    const nextFilters: DocumentFilters = { ...appliedFilters, name: null };
    dispatch(setAppliedFilters(nextFilters));
    setSearchParams(serializeFiltersToUrl(searchParams, nextFilters), {
      replace: true,
    });
    dispatch(
      fetchAvailableDocuments({
        roomId: dataRoom!.id,
        folderId: currentFolder[0]?.id || null,
        filters: nextFilters,
      }),
    );
    close();
  };

  return (
    <div
      className={classes.filterDropdown}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <Input
        ref={searchInput}
        placeholder={t('Documents.management.search')}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        onPressEnter={handleSearch}
        className={classes.filterInput}
      />
      <Space>
        <Button
          type='primary'
          onClick={handleSearch}
          icon={<SearchOutlined />}
          size='small'
        >
          {t('Documents.management.search_button')}
        </Button>
        <Button onClick={handleReset} size='small'>
          {t('Documents.management.search_reset')}
        </Button>
        <Button
          type='text'
          size='small'
          icon={<Assets.CloseIcon className={classes.greyIcon} />}
          onClick={close}
        />
      </Space>
    </div>
  );
};

export default FilterDropdown;
